<template>
  <div class="px-8 py-8 bg-white">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-gray-900">WhatsApp Insights</h1>

      <div class="flex items-center gap-3">
        <select
          v-model.number="days"
          class="h-9 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none"
          @change="fetchInsights"
        >
          <option :value="7">Last 7 days</option>
          <option :value="30">Last 30 days</option>
          <option :value="90">Last 90 days</option>
        </select>

        <span v-if="data" class="text-xs text-gray-400">
          {{ data.range.start }} - {{ data.range.end }}
        </span>
      </div>
    </div>

    <div v-if="loading" class="text-center text-sm text-gray-400 py-20">
      Loading...
    </div>

    <div v-else-if="data" class="grid grid-cols-12 gap-6">
      <!-- All Messages -->
      <div class="col-span-12 lg:col-span-4 card">
        <h2 class="card-title mb-4">All Messages</h2>
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <span class="row-left">Messages sent</span>
            <span class="row-right">{{ data.all_messages.sent }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="row-left">Messages delivered</span>
            <span class="row-right">{{ data.all_messages.delivered }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="row-left">Messages received</span>
            <span class="row-right">{{ data.all_messages.received }}</span>
          </div>
        </div>
      </div>

      <!-- Messages Delivered by category -->
      <div class="col-span-12 lg:col-span-4 card">
        <h2 class="card-title mb-4">Messages Delivered</h2>
        <div v-if="categoryRows.length" class="space-y-3">
          <div
            v-for="row in categoryRows"
            :key="row.key"
            class="flex items-center justify-between"
          >
            <span class="row-left">{{ row.label }}</span>
            <span class="row-right">{{ row.value }}</span>
          </div>
        </div>
        <p v-else class="text-sm text-gray-400">
          No category data for this range.
        </p>
      </div>

      <!-- Free Messages Delivered -->
      <div class="col-span-12 lg:col-span-4 card">
        <h2 class="card-title mb-4">Free Messages Delivered</h2>
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <span class="row-left">Free customer service</span>
            <span class="row-right">{{
              data.free_messages_delivered.FREE_CUSTOMER_SERVICE
            }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="row-left">Free entry point</span>
            <span class="row-right">{{
              data.free_messages_delivered.FREE_ENTRY_POINT
            }}</span>
          </div>
        </div>
      </div>

      <!-- Paid Messages Delivered by category -->
      <div class="col-span-12 lg:col-span-6 card">
        <h2 class="card-title mb-4">Paid Messages Delivered</h2>
        <div v-if="paidCategoryRows.length" class="space-y-3">
          <div
            v-for="row in paidCategoryRows"
            :key="row.key"
            class="flex items-center justify-between"
          >
            <span class="row-left">{{ row.label }}</span>
            <span class="row-right">{{ row.value }}</span>
          </div>
        </div>
        <p v-else class="text-sm text-gray-400">
          No paid messages in this range.
        </p>
      </div>

      <!-- Approximate Total Charges -->
      <div class="col-span-12 lg:col-span-6 card">
        <div class="flex items-center justify-between mb-4">
          <h2 class="card-title">Approximate Total Charges</h2>
          <span class="text-[18px] font-bold text-gray-900">
            {{ formatMoney(data.total_charges) }} {{ data.currency }}
          </span>
        </div>
        <div v-if="chargeRows.length" class="space-y-3">
          <div
            v-for="row in chargeRows"
            :key="row.key"
            class="flex items-center justify-between"
          >
            <span class="row-left">{{ row.label }}</span>
            <span class="row-right"
              >{{ formatMoney(row.value) }} {{ data.currency }}</span
            >
          </div>
        </div>
        <p v-else class="text-sm text-gray-400">No charges in this range.</p>
      </div>
    </div>

    <div v-else class="text-center text-sm text-gray-400 py-20">
      Failed to load insights.
    </div>
  </div>
</template>

<script>
import axios from "axios";

const marketingApi = axios.create({
  baseURL: "https://marketingvps.lyon-jo.com/api",
});
marketingApi.interceptors.request.use((config) => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("access_token");
  if (token) config.headers.Authorization = "Bearer " + token;
  return config;
});

const CATEGORY_LABELS = {
  MARKETING: "Marketing",
  UTILITY: "Utility",
  AUTHENTICATION: "Authentication",
  AUTHENTICATION_INTERNATIONAL: "Authentication - international",
  SERVICE: "Service",
  MARKETING_LITE: "Marketing Lite",
  MARKETING_LITE_DYNAMIC: "Marketing Lite (dynamic)",
  AI_BOT: "AI Provider",
  GROUP_MARKETING: "Group Marketing",
  GROUP_UTILITY: "Group Utility",
  GROUP_SERVICE: "Group Service",
  GROUP_MARKETING_LITE: "Group Marketing Lite",
};

export default {
  name: "Dashboard",

  data() {
    return {
      loading: false,
      data: null,
      days: 7,
    };
  },

  computed: {
    categoryRows() {
      return this.toRows(this.data?.messages_delivered_by_category);
    },
    paidCategoryRows() {
      return this.toRows(this.data?.paid_messages_delivered_by_category);
    },
    chargeRows() {
      return this.toRows(this.data?.total_charges_by_category);
    },
  },

  methods: {
    toRows(obj) {
      if (!obj) return [];
      return Object.keys(obj)
        .filter((k) => obj[k] > 0)
        .map((k) => ({
          key: k,
          label: CATEGORY_LABELS[k] || k,
          value: obj[k],
        }));
    },
    formatMoney(v) {
      const n = Number(v || 0);
      return n.toFixed(2);
    },
    async fetchInsights() {
      this.loading = true;
      try {
        const res = await marketingApi.get("/whatsapp/insights", {
          params: { days: this.days },
        });
        this.data = res.data;
      } catch (e) {
        console.error("fetchInsights failed:", e);
        this.data = null;
      } finally {
        this.loading = false;
      }
    },
  },

  mounted() {
    this.fetchInsights();
  },
};
</script>

<style scoped>
.card {
  background: #f9f9f9;
  border-radius: 20px;
  padding: 20px 30px;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.06), 0 10px 20px rgba(0, 0, 0, 0.08);
}

.card-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.row-left {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.row-right {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}
</style>
